import React from "react";
import gql from "graphql-tag";
//Limitless imports
import { useExpertMessageContext } from "../..";
import { useGraphAPIHook } from "../../../portal";
import { MESSAGE } from "../../../portal/apis/queries/MessageQueries";
import { isValidReviewCriteria, validateNote } from "./ExpertMessageQualityReviewHelper";
import { calculateReviewSummary, prepareReviewCriteria } from "@limitless/portal";
const COMPLETE_QUALITY_REVIEW = (isAIDraftSelected, feedback, isFeedbackOptinal) => {
  let schema = `$messageId: String!, $reviewCriteriaScore: [ReviewCriteriaScoreInput]`;
  let type = `messageId: $messageId, reviewCriteriaScore: $reviewCriteriaScore`;
  if (isFeedbackOptinal) {
    schema += ", $reviewNotes: String";
    type += ", reviewNotes: $reviewNotes";
  }
  if (isAIDraftSelected) {
    schema += ", $isAIDraftSelected: Boolean";
    type += ", isAIDraftSelected: $isAIDraftSelected";
  }
  if (feedback !== "") {
    schema += ", $feedback: String";
    type += ", feedback: $feedback";
  }
  return gql`
        mutation completeDetailedReview(${schema}) {
            completeDetailedReview(${type}) {
                ${MESSAGE}
            }
        }
    `;
};
const INITIAL_REVIEW_STATE = {
  isReviewComplete: false,
  reviewCriteria: [],
  reviewNote: "",
  reviewNoteError: ""
};
const INITIAL_FEEDBACK_ISAIDRAFTSELECTED = {
  isAIDraftSelected: false,
  feedback: ""
};
const INITIAL_ARRAY = {
  aiReviweCriteria: []
};
export const useMessageQualityReview = () => {
  const [reviewState, setReviewState] = React.useState(INITIAL_REVIEW_STATE);
  const [feedbackIsAIDraftSelected, setFeedbackIsAIDraftSelected] = React.useState(INITIAL_FEEDBACK_ISAIDRAFTSELECTED);
  const [updatedReviewCriteriaByAI, setUpdatedReviewCriteriaByAI] = React.useState(INITIAL_ARRAY);
  const {
    message,
    clearMessageAndUnclaim
  } = useExpertMessageContext();
  const {
    mutateData
  } = useGraphAPIHook();
  React.useEffect(() => {
    if (message.messageId && message.practiceGroup) {
      const reviewState = prepareReviewCriteria(message, reviewSummary);
      setReviewState(reviewState);
    }
  }, [message.messageId]);
  const reviewSummary = React.useMemo(() => calculateReviewSummary(message, false), [message.messageId]);
  const {
    hasCorrectReply
  } = reviewSummary;
  const isFeedbackOptinal = message.practiceGroup ? hasCorrectReply && message.onboarding.responseType === "dia" : true;
  React.useMemo(() => {
    const isCriteriaValid = isValidReviewCriteria(reviewState.reviewCriteria);
    const {
      isNoteValid,
      noteError
    } = validateNote(reviewState.reviewNote);
    if (feedbackIsAIDraftSelected.isAIDraftSelected) {
      let allMatched = reviewState.reviewCriteria.every(e => {
        const matchedItem = updatedReviewCriteriaByAI.aiReviweCriteria.reviewCriteria.find(s => s.reviewCriteriaId === e.reviewCriteriaId);
        return matchedItem && matchedItem.score === e.score;
      });
      if (feedbackIsAIDraftSelected.feedback !== "" && !allMatched) {
        allMatched = true;
      }
      setReviewState(prevState => ({
        ...prevState,
        isReviewComplete: allMatched
      }));
    } else {
      setReviewState({
        ...reviewState,
        isReviewComplete: isNoteValid && isCriteriaValid,
        reviewNoteError: noteError
      });
    }
  }, [reviewState.reviewCriteria, reviewState.reviewNote, feedbackIsAIDraftSelected.feedback]);
  const updateQualityReviewCriteria = (reviewCriteriaId, score, text) => {
    const updatedReviewCriteria = [...reviewState.reviewCriteria];
    updatedReviewCriteria.map(rc => {
      if (rc.reviewCriteriaId === reviewCriteriaId && score !== null) {
        rc.score = parseInt(score);
      }
      if (rc.reviewCriteriaId === reviewCriteriaId && score === null) {
        rc.score = null;
      }
      if (rc.reviewCriteriaId === reviewCriteriaId && text !== null) {
        rc.text = text;
      }
    });
    setReviewState({
      ...reviewState,
      reviewCriteria: updatedReviewCriteria
    });
  };
  const updateQualityReviewNote = newNote => {
    setReviewState({
      ...reviewState,
      reviewNote: newNote
    });
  };
  const updateReviewCriteriaByAI = newai => {
    setUpdatedReviewCriteriaByAI({
      ...updatedReviewCriteriaByAI,
      aiReviweCriteria: newai
    });
  };
  const updateIsAIDraftSelected = isSelected => {
    setFeedbackIsAIDraftSelected({
      ...feedbackIsAIDraftSelected,
      isAIDraftSelected: isSelected
    });
  };
  const updateFeedback = newFeedback => {
    setFeedbackIsAIDraftSelected({
      ...feedbackIsAIDraftSelected,
      feedback: newFeedback
    });
  };
  const updateReviewSchemeText = (reviewCriteriaId, newText) => {
    const updatedReviewCriteria = [...reviewState.reviewCriteria];
    updatedReviewCriteria.map(rc => {
      if (rc.reviewCriteriaId === reviewCriteriaId) {
        rc.text = newText;
      }
    });
    setReviewState({
      ...reviewState,
      reviewCriteria: updatedReviewCriteria
    });
  };
  const saveQualityReview = () => {
    const {
      isAIDraftSelected,
      feedback
    } = feedbackIsAIDraftSelected;
    const reviewCriteriaScore = reviewState.reviewCriteria.map(item => {
      const text = item.text && item.text !== "" ? item.text : null;
      return {
        score: item.score,
        reviewCriteriaId: item.reviewCriteriaId,
        text
      };
    });
    const variables = {
      messageId: message.messageId,
      reviewCriteriaScore
    };
    if (isFeedbackOptinal) {
      variables.reviewNotes = reviewState.reviewNote;
    }
    if (isAIDraftSelected) {
      variables.isAIDraftSelected = isAIDraftSelected;
    }
    if (feedback !== "") {
      variables.feedback = feedback;
    }
    const saveQualityReviewAction = {
      mutation: COMPLETE_QUALITY_REVIEW(isAIDraftSelected, feedback, isFeedbackOptinal),
      variables,
      responseKey: "qualityReviewQuestion",
      notifyDetails: {
        actionName: "Quality Review",
        errorNotification: "Failed to save detailed review",
        successNotification: "Detailed Review Completed"
      },
      postSuccessFunction: () => clearMessageAndUnclaim()
    };
    mutateData(saveQualityReviewAction);
  };
  return {
    saveQualityReview,
    updateQualityReviewCriteria,
    updateQualityReviewNote,
    updateReviewSchemeText,
    reviewState,
    setReviewState,
    feedbackIsAIDraftSelected,
    setFeedbackIsAIDraftSelected,
    updateIsAIDraftSelected,
    updateFeedback,
    updateReviewCriteriaByAI,
    setUpdatedReviewCriteriaByAI,
    updatedReviewCriteriaByAI
  };
};